import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePage, Link } from '@inertiajs/react';
import { cn } from '../../lib/cn';
import Sidebar, { getTeacherNavItems, getStudentNavItems } from '../../components/layout/Sidebar';
import ImpersonationBanner from '../../components/layout/ImpersonationBanner';
import ThemeToggle from '../../components/ui/ThemeToggle';
import AtlasMap from '../../components/atlas/AtlasMap';
import TimeSlider from '../../components/atlas/TimeSlider';
import OverlayPanel from '../../components/atlas/OverlayPanel';
import EntityTooltip from '../../components/atlas/EntityTooltip';
import MapLegend from '../../components/atlas/MapLegend';
import MapConfigSaveModal from '../../components/atlas/MapConfigSaveModal';
import Logo from '../../components/ui/Logo';

// Stable reference for map center — avoids re-renders resetting zoom
const MAP_CENTER = [10, 20];

// Cliopatria snapshot years available for AP World History: Modern (1200–2010)
const AP_WORLD_YEARS = [
  1200, 1279, 1300, 1400, 1492, 1500, 1530, 1600, 1650, 1700,
  1715, 1783, 1800, 1815, 1880, 1900, 1914, 1920, 1930, 1938,
  1945, 1960, 1994, 2000, 2010,
];

// Available map data sets — each corresponds to a course curriculum
const MAP_SETS = [
  {
    id: 'ap_world_modern',
    label: 'AP World History: Modern',
    availableYears: AP_WORLD_YEARS,
    defaultYear: 1300,
    defaultOverlays: ['silk_road', 'major_cities_1200_1450'],
    snapshotPath: (year) => `/data/atlas/cliopatria/world_${year}.geojson`,
  },
];

export default function AtlasIndex({ regions, overlays, mapConfigs: initialMapConfigs, courses }) {
  const { t } = useTranslation();
  const { auth } = usePage().props;
  const user = auth?.user;
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';
  const navItems = isTeacher ? getTeacherNavItems(user?.role, t) : getStudentNavItems(t);

  // Map set selection — null means plain political map
  const [selectedMapSet, setSelectedMapSet] = useState(null);

  // Map state (only active when a map set is selected)
  const [selectedYear, setSelectedYear] = useState(1300);
  const [geoData, setGeoData] = useState(null);
  const [activeOverlays, setActiveOverlays] = useState([]);
  const [overlayData, setOverlayData] = useState({ tradeRoutes: null, cities: null, battles: null });
  const [hoveredEntity, setHoveredEntity] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showLegend, setShowLegend] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [mapConfigs, setMapConfigs] = useState(initialMapConfigs || []);
  const [visibleEntities, setVisibleEntities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cache for loaded snapshot files to avoid re-fetching
  const snapshotCache = useRef({});

  const mapSetActive = selectedMapSet !== null;

  // When a map set is selected/deselected, reset state
  const handleMapSetChange = useCallback((setId) => {
    if (setId === '') {
      setSelectedMapSet(null);
      setGeoData(null);
      setActiveOverlays([]);
      setOverlayData({ tradeRoutes: null, cities: null, battles: null });
      setVisibleEntities([]);
      return;
    }
    const mapSet = MAP_SETS.find((s) => s.id === setId);
    if (mapSet) {
      setSelectedMapSet(mapSet);
      setSelectedYear(mapSet.defaultYear);
      setActiveOverlays(mapSet.defaultOverlays || []);
    }
  }, []);

  // Load Cliopatria snapshot when selectedYear changes (only when a map set is active)
  useEffect(() => {
    if (!mapSetActive || !selectedMapSet?.snapshotPath) {
      if (!mapSetActive) setGeoData(null);
      return;
    }

    const url = selectedMapSet.snapshotPath(selectedYear);

    // Check cache first
    if (snapshotCache.current[url]) {
      setGeoData(snapshotCache.current[url]);
      return;
    }

    setLoading(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load snapshot');
        return res.json();
      })
      .then((data) => {
        snapshotCache.current[url] = data;
        setGeoData(data);
        setLoading(false);
      })
      .catch(() => {
        setGeoData(null);
        setLoading(false);
      });
  }, [selectedYear, mapSetActive, selectedMapSet]);

  // Update visible entities list for the legend (all named features in snapshot)
  useEffect(() => {
    if (!geoData?.features) {
      setVisibleEntities([]);
      return;
    }
    const visible = geoData.features
      .filter((f) => f.properties?.NAME)
      .map((f) => f.properties);
    setVisibleEntities(visible);
  }, [geoData]);

  // Load overlay data from seeded overlays (only when map set active)
  useEffect(() => {
    if (!mapSetActive) return;

    const tradeOverlay = overlays.find((o) => activeOverlays.includes(o.slug) && o.overlay_type === 'trade_route');
    const cityOverlay = overlays.find((o) => activeOverlays.includes(o.slug) && o.overlay_type === 'city');
    const battleOverlay = overlays.find((o) => activeOverlays.includes(o.slug) && o.overlay_type === 'battle');

    const fetchOverlayData = async (overlay) => {
      if (!overlay) return null;
      try {
        const res = await fetch(`/atlas/overlays/${overlay.id}`);
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    };

    Promise.all([
      fetchOverlayData(tradeOverlay),
      fetchOverlayData(cityOverlay),
      fetchOverlayData(battleOverlay),
    ]).then(([tradeRoutes, cities, battles]) => {
      setOverlayData({ tradeRoutes, cities, battles });
    });
  }, [activeOverlays, overlays, mapSetActive]);

  const handleEntityHover = useCallback((properties, position) => {
    setHoveredEntity(properties);
    if (position) setTooltipPos(position);
  }, []);

  const handleEntityClick = useCallback((properties) => {
    if (properties?.WikipediaURL) {
      window.open(properties.WikipediaURL, '_blank', 'noopener');
    }
  }, []);

  const handleOverlayToggle = useCallback((slug) => {
    setActiveOverlays((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  }, []);

  const handleConfigSelect = useCallback((config) => {
    if (config.year_focus) setSelectedYear(config.year_focus);
    if (config.active_overlays?.length) setActiveOverlays(config.active_overlays);
  }, []);

  const handleSaveConfig = useCallback((newConfig) => {
    setMapConfigs((prev) => [newConfig, ...prev]);
  }, []);

  // Map projection settings — constant, defined outside render to avoid ref changes
  const mapCenter = MAP_CENTER;
  const mapScale = 250;
  const mapProjection = 'geoNaturalEarth1';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      <ImpersonationBanner />
      <div className="flex h-screen overflow-hidden">
        <Sidebar items={navItems} />

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top bar */}
          <header className="flex-shrink-0 h-14 bg-white/80 backdrop-blur-md border-b border-gray-200 dark:bg-gray-900/80 dark:border-gray-700 z-30">
            <div className="flex items-center justify-between h-full px-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Link href={isTeacher ? '/teacher/dashboard' : '/'} className="flex items-center gap-2 flex-shrink-0">
                  <Logo className="h-9" />
                </Link>
                <div className="flex items-center gap-2 pl-3 border-l border-gray-300 dark:border-gray-600">
                  <span data-testid="atlas-page-label" className="text-sm font-medium text-gray-900 dark:text-white">{t('nav.atlas')}</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {isTeacher && (
                  <Link
                    href="/teacher/atlas_map_sets"
                    className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {t('nav.atlasMapSets')}
                  </Link>
                )}

                {/* Map set selector — always visible */}
                <div className="relative">
                  <select
                    value={selectedMapSet?.id || ''}
                    onChange={(e) => handleMapSetChange(e.target.value)}
                    className={cn(
                      'appearance-none bg-none cursor-pointer',
                      'bg-white dark:bg-gray-800',
                      'border border-gray-300 dark:border-gray-600',
                      'rounded-lg px-3 py-2 pr-8',
                      'text-sm font-medium text-gray-800 dark:text-gray-200',
                      'shadow-sm hover:border-gray-400 dark:hover:border-gray-500',
                      'focus:outline-none focus:ring-2 focus:ring-gray-400/50'
                    )}
                    style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                  >
                    <option value="">{t('atlas.politicalMap')}</option>
                    {MAP_SETS.map((ms) => (
                      <option key={ms.id} value={ms.id}>
                        {ms.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>

                {/* Region selector — disabled for now, world view only */}

                {mapSetActive && mapConfigs.length > 0 && (
                  <select
                    onChange={(e) => {
                      const config = mapConfigs.find((c) => c.id === parseInt(e.target.value));
                      if (config) handleConfigSelect(config);
                    }}
                    defaultValue=""
                    className={cn(
                      'appearance-none cursor-pointer',
                      'bg-white dark:bg-gray-800',
                      'border border-gray-300 dark:border-gray-600',
                      'rounded-lg px-3 py-2 pr-8',
                      'text-sm text-gray-800 dark:text-gray-200',
                      'shadow-sm hover:border-gray-400',
                      'focus:outline-none focus:ring-2 focus:ring-gray-400/50'
                    )}
                  >
                    <option value="" disabled>
                      {t('atlas.savedMaps')}
                    </option>
                    {mapConfigs.map((config) => (
                      <option key={config.id} value={config.id}>
                        {config.name}
                      </option>
                    ))}
                  </select>
                )}

                {mapSetActive && isTeacher && (
                  <button
                    onClick={() => setShowSaveModal(true)}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm font-medium',
                      'bg-gray-900 dark:bg-white text-white dark:text-gray-900',
                      'hover:bg-gray-800 dark:hover:bg-gray-100',
                      'shadow-sm transition-colors'
                    )}
                  >
                    {t('atlas.saveMap')}
                  </button>
                )}

                <ThemeToggle />
              </div>
            </div>
          </header>

          {/* Map viewport */}
          <div className="flex-1 relative overflow-hidden">
            {loading ? (
              <div className="w-full h-full flex items-center justify-center" style={{ background: '#dce7f0' }}>
                <div className="text-center">
                  <div className="inline-block w-8 h-8 border-2 border-gray-400 border-t-gray-800 rounded-full animate-spin" />
                  <p className="mt-3 text-sm text-gray-500">{t('atlas.loadingMapData')}</p>
                </div>
              </div>
            ) : (
              <AtlasMap
                geoData={mapSetActive ? geoData : null}
                selectedYear={selectedYear}
                overlayData={mapSetActive ? overlayData : null}
                center={mapCenter}
                scale={mapScale}
                projection={mapProjection}
                onEntityHover={handleEntityHover}
                onEntityClick={handleEntityClick}
              />
            )}

            {/* Overlay panel — only when map set active */}
            {mapSetActive && (
              <OverlayPanel
                overlays={overlays}
                activeOverlays={activeOverlays}
                onToggle={handleOverlayToggle}
                selectedYear={selectedYear}
              />
            )}

            {/* Legend — only when map set active */}
            {mapSetActive && (
              <MapLegend
                entities={visibleEntities}
                visible={showLegend}
                onToggle={() => setShowLegend(!showLegend)}
              />
            )}

            {/* Legend toggle when hidden */}
            {mapSetActive && !showLegend && (
              <button
                onClick={() => setShowLegend(true)}
                className={cn(
                  'absolute bottom-20 right-4 z-10',
                  'bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm',
                  'border border-gray-200 dark:border-gray-700',
                  'rounded-lg shadow-sm px-3 py-1.5',
                  'text-xs font-medium text-gray-600 dark:text-gray-300',
                  'hover:bg-white dark:hover:bg-gray-800 transition-colors'
                )}
              >
                {t('atlas.showLegend')}
              </button>
            )}

            {/* Entity tooltip */}
            <EntityTooltip entity={hoveredEntity} position={tooltipPos} />
          </div>

          {/* Time slider — only when map set active */}
          {mapSetActive && (
            <TimeSlider
              value={selectedYear}
              onChange={setSelectedYear}
              availableYears={selectedMapSet?.availableYears || []}
            />
          )}
        </div>
      </div>

      {/* Save modal */}
      {mapSetActive && (
        <MapConfigSaveModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveConfig}
          courses={courses}
          selectedRegion={regions.find((r) => r.slug === 'world') || regions[0]}
          yearStart={selectedMapSet?.availableYears?.[0] || 1200}
          yearEnd={selectedMapSet?.availableYears?.at(-1) || 2010}
          yearFocus={selectedYear}
          activeOverlays={activeOverlays}
        />
      )}
    </div>
  );
}
